// Server-side preferences — the two flags that actually change how Oreia thinks.
//
// These are not sealed. Everything the vault protects is content; these are switches, and the Worker
// has to read them itself to decide whether to build memory at all (see collective-vault-worker's
// settings.ts). Storing them encrypted would mean the server could not honour them.
//
// The web app never called this endpoint, so both toggles have been reachable only from the phone
// even though the pipeline they control is shared.

import { apiGet, apiPut } from './client';

export interface UserSettings {
  /** Keep journaling, but stop deriving new observations from it. */
  memoryPaused: boolean;
  /** Let dreams and waking entries be read against each other. */
  linkingEnabled: boolean;
  /** Stored but unused in v1 — kept so a round-trip does not drop it. */
  surfacingLevel: string;
}

/**
 * Snake_case as the Worker stores it. An index signature because the request helpers take a plain
 * JSON record; keeping the named fields alongside it still catches a typo in a key.
 */
interface SettingsWire extends Record<string, unknown> {
  memory_paused?: boolean;
  linking_enabled?: boolean;
  surfacing_level?: string;
}

/** Defaults match the Worker's SETTINGS_DEFAULTS: a missing row means memory on, linking on. */
export const SETTINGS_DEFAULTS: UserSettings = {
  memoryPaused: false,
  linkingEnabled: true,
  surfacingLevel: 'gentle',
};

function fromWire(wire: SettingsWire): UserSettings {
  return {
    memoryPaused: wire.memory_paused ?? SETTINGS_DEFAULTS.memoryPaused,
    linkingEnabled: wire.linking_enabled ?? SETTINGS_DEFAULTS.linkingEnabled,
    surfacingLevel: wire.surfacing_level ?? SETTINGS_DEFAULTS.surfacingLevel,
  };
}

export async function fetchSettings(signal?: AbortSignal): Promise<UserSettings> {
  return fromWire(await apiGet<SettingsWire>('/api/settings', signal));
}

/**
 * Save a subset. The Worker merges over current state and returns the whole thing, so the response
 * is the new truth rather than an echo of the patch — worth using, since it catches a value the
 * server rejected or normalised.
 */
export async function updateSettings(
  patch: Partial<UserSettings>,
  signal?: AbortSignal,
): Promise<UserSettings> {
  const wire: SettingsWire = {};
  if (patch.memoryPaused !== undefined) wire.memory_paused = patch.memoryPaused;
  if (patch.linkingEnabled !== undefined) wire.linking_enabled = patch.linkingEnabled;
  if (patch.surfacingLevel !== undefined) wire.surfacing_level = patch.surfacingLevel;
  return fromWire(await apiPut<SettingsWire>('/api/settings', wire, signal));
}
